import { Code, ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Button from 'material/components/Button';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setSnackbarError } from 'store/actions/global';
import { useCopyToClipboard } from 'utils/hooks';
import { usStates } from 'utils/constants';
import { StyledCode } from 'globalStyles';
import WidgetDialog from 'routes/ActionCenterDetails/components/WidgetDialog';
import {
  ActionButtonsWrapper,
  InputsRow,
  InputWrapper,
  OfficialsLookupWidgetCheckboxWrapper,
  OptionsSection,
  OptionsToggle,
  PreviewWrapper,
  StyledCard,
  StyledFormControl,
  WidgetDescription,
  WidgetTitle,
} from './styles';

const OfficialsLookupWidget = () => {
  const dispatch = useDispatch();
  const [copy] = useCopyToClipboard();
  const { organizationUuid } = useParams();
  const [showOptions, setShowOptions] = useState(false);
  const [federal, setFederal] = useState(true);
  const [state, setState] = useState(true);
  const [selectedState, setSelectedState] = useState('ALL');
  const [width, setWidth] = useState('100');
  const [height, setHeight] = useState('1000');
  const [fullHeight, setFullHeight] = useState(false);
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const getWidgetCode = () => {
    const { REACT_APP_LANDING_PAGE_URL, REACT_APP_OFFICIALS_WIDGET_URL } = process.env;

    let dataCategory = '';

    if (federal && !state) dataCategory = 'federal';

    if (!federal && state) dataCategory = 'state';

    const dataState = selectedState === 'ALL' ? '' : selectedState;

    return `
      <div
        data-org-id="${organizationUuid}"
        data-domain="${REACT_APP_LANDING_PAGE_URL || 'https://mstr.app'}"
        data-height="${height}"
        data-width="${width}%"
        data-height-full="${fullHeight ? 'true' : 'false'}"
        data-css="${customCssUrl}"
        data-state="${dataState}"
        data-category="${dataCategory}"
        id="muster-lookup"
      >
        <script src="${REACT_APP_OFFICIALS_WIDGET_URL || 'https://widgets.muster.com/v2/officials-20250624.js'}"></script>
      </div>
    `;
  };

  const validate = () => {
    if (customCssUrl && (!customCssUrl.startsWith('https') || !customCssUrl.endsWith('.css'))) {
      dispatch(
        setSnackbarError({ message: 'Custom CSS URL must start with https and end with .css' })
      );

      return false;
    }

    return true;
  };

  const handleCopyToClipboard = () => {
    if (!validate()) return;

    copy(getWidgetCode(), 'textarea', 'Code');
  };

  const handleViewCode = () => {
    if (!validate()) return;

    setIsDialogOpen(true);
  };

  const handleDialogCopyToClipboard = () => {
    copy(getWidgetCode(), 'textarea', 'Code');
    setIsDialogOpen(false);
  };

  return (
    <>
      <StyledCard>
        <WidgetTitle variant="h6">Officials Lookup Widget</WidgetTitle>
        <WidgetDescription variant="body2">
          Allow advocates to lookup their officials on your website. Optionally limit to one level
          (federal, state) or one specific state.
        </WidgetDescription>

        <PreviewWrapper>
          <img
            alt="Action center widget preview"
            height="200px"
            src="/images/widget-preview-officials-lookup.webp"
          />
        </PreviewWrapper>

        <OptionsToggle onClick={handleOptionsToggle}>
          {showOptions ? 'Hide options' : 'Options'}
          {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </OptionsToggle>

        {showOptions && (
          <OptionsSection>
            <OfficialsLookupWidgetCheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={federal}
                    onChange={e => setFederal(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Federal</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state}
                    onChange={e => setState(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">State</Typography>}
              />
            </OfficialsLookupWidgetCheckboxWrapper>

            <InputsRow>
              <InputWrapper>
                <StyledFormControl variant="outlined">
                  <InputLabel htmlFor="state-widget-select" sx={{ backgroundColor: 'white' }}>
                    State
                  </InputLabel>
                  <Select
                    fullWidth={false}
                    variant="outlined"
                    native
                    value={selectedState}
                    onChange={e => setSelectedState(e.target.value)}
                    label="State"
                    inputProps={{
                      id: 'state-widget-select',
                      name: 'state',
                    }}
                  >
                    <option value="ALL">All states</option>
                    {usStates.map(usState => (
                      <option key={usState.abbreviation} value={usState.abbreviation}>
                        {usState.name}
                      </option>
                    ))}
                  </Select>
                </StyledFormControl>
              </InputWrapper>
            </InputsRow>

            <InputsRow>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Width (%)"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-width`"
                />
              </InputWrapper>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Height (pixels)"
                  value={height}
                  onChange={e => setHeight(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-height`"
                />
              </InputWrapper>
            </InputsRow>

            <OfficialsLookupWidgetCheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullHeight}
                    onChange={e => setFullHeight(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Full height</Typography>}
              />
              <FormHelperText
                sx={{
                  marginLeft: '14px',
                  marginRight: '14px',
                  marginTop: '3px',
                }}
              >
                Populates `data-height-full`
              </FormHelperText>
            </OfficialsLookupWidgetCheckboxWrapper>

            <InputWrapper>
              <TextField
                fullWidth
                label="Custom CSS (Optional)"
                value={customCssUrl}
                onChange={e => setCustomCssUrl(e.target.value)}
                variant="outlined"
                size="medium"
                placeholder="https://example.com/styles.css"
              />
            </InputWrapper>
          </OptionsSection>
        )}

        <ActionButtonsWrapper>
          <Button variant="text" startIcon={<ContentCopy />} onClick={handleCopyToClipboard}>
            Copy to Clipboard
          </Button>
          <Button variant="contained" startIcon={<Code />} onClick={handleViewCode}>
            View Code
          </Button>
        </ActionButtonsWrapper>
      </StyledCard>
      <WidgetDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={() => setIsDialogOpen(false)}
        title="Widget Code"
        handleCopyToClipboard={handleDialogCopyToClipboard}
      >
        <StyledCode>{getWidgetCode()}</StyledCode>
      </WidgetDialog>
    </>
  );
};

export default OfficialsLookupWidget;
